const img = (name, w, h, alt) => ({
  src: `/img/${name}-full.webp`,
  srcSet: `/img/${name}-480.webp 480w, /img/${name}-full.webp ${w}w`,
  width: w,
  height: h,
  alt,
});

const sharpImg = (name, w, h, alt) => ({ src: `/img/${name}.png`, width: w, height: h, alt });

const ILLUSTRATIVE = "Visuel illustratif";
const PLAN_NOTE = "Cotes indicatives — non validé pour la fabrication";

export const STUDIES = [
  {
    id: "convoyeur-tremie",
    num: "04",
    featured: true,
    title: "Convoyeur industriel et trémie",
    subtitle: "Étude type de manutention de matières en vrac",
    contactType: "conception",
    nature: "Fiche générée · étude illustrative",
    need: "Étudier l'implantation d'un convoyeur incliné et d'une trémie dans un atelier, selon le flux de matière et les interfaces disponibles.",
    work: "Cette fiche générée illustre un concept 3D et des aperçus de plans. Le dimensionnement, les cotes, les matières et la fabrication restent à étudier et à valider pour tout projet réel.",
    workLabel: "Concept illustré.",
    workHeading: "Concept illustré",
    visualWarning: "Concept illustratif — détails techniques à valider.",
    hero: { src: "/img/convoyeur-tremie-fiche-illustrative.jpg", width: 1792, height: 2400, alt: "Fiche illustrative générée montrant un convoyeur incliné, une trémie, un concept 3D et des aperçus de plans non validés", label: "Fiche projet 04", note: "Visuel généré · non validé" },
    plan: null,
    hidePlan: true,
    visuals: [
      { src: "/img/convoyeur-tremie-fiche-illustrative.jpg", width: 1792, height: 2400, alt: "Fiche illustrative générée d'un convoyeur et d'une trémie, sans validation technique ni réalisation attestée", kind: "document", label: "Convoyeur et trémie", note: "Image générée : réalisation, cotes et fabrication non vérifiées" },
    ],
    steps: [
      { label: "Cadrage du besoin", text: "Flux de matière, implantation et interfaces à préciser.", visual: null },
      { label: "Concept 3D", text: "Agencement illustratif du convoyeur et de la trémie.", visual: null },
      { label: "Validation technique", text: "Calculs, plans et conformité à établir pour un projet réel.", visual: null },
    ],
    deliverablesLabel: "Éléments présentés",
    deliverables: ["Concept 3D illustratif", "Aperçus de plans non validés"],
    deliverablesNote: "Image générée ; aucun plan, cote, matériau ou résultat de fabrication n'est validé.",
    missing: null,
  },
  {
    id: "structure-metallique",
    num: "01",
    title: "Structure métallique",
    subtitle: "Plateforme d'accès industrielle",
    contactType: "structure",
    nature: "Étude type · visuels illustratifs",
    need:
      "Concevoir ou reprendre une plateforme d'accès simple — ossature en profilés, plancher caillebotis, garde-corps et escalier — destinée à un atelier de production.",
    work:
      "Modélisation 3D de l'ossature et des accès, définition des interfaces au sol et des assemblages, puis préparation du dossier technique : plan d'ensemble et nomenclature.",
    hero: { ...sharpImg("plateforme-cao-net", 1254, 1254, "Rendu CAO d'une plateforme d'accès avec escalier et garde-corps"), label: "Rendu CAO", note: ILLUSTRATIVE },
    plan: { ...img("plateforme-plan", 740, 430, "Aperçu de plan d'ensemble de la plateforme : vues de face, de côté et de dessus"), label: "Aperçu de plan", note: PLAN_NOTE },
    visuals: [
      { ...img("plateforme-photo", 614, 433, "Plateforme d'accès implantée dans un atelier"), kind: "photo", label: "Implantation en atelier", note: ILLUSTRATIVE },
      { ...sharpImg("plateforme-cao-net", 1254, 1254, "Rendu CAO de la plateforme d'accès"), kind: "cao", label: "Rendu CAO", note: ILLUSTRATIVE },
      { ...img("plateforme-plan", 740, 430, "Aperçu de plan de la plateforme d'accès"), kind: "plan", label: "Aperçu de plan", note: PLAN_NOTE },
      { ...sharpImg("plateforme-fiche-4000x4000", 1086, 1448, "Fiche illustrative d'une plateforme carrée de 4 000 × 4 000 mm : rendus métal et CAO, vues de principe cotées, hauteurs à confirmer et charge admissible à déterminer par calcul"), kind: "document", label: "Fiche plateforme 4 × 4 m", note: "Variante illustrative · non validée pour la fabrication" },
    ],
    steps: [
      { label: "Besoin / relevé", text: "Implantation, accès et contraintes d'atelier.", visual: 0 },
      { label: "Conception CAO", text: "Ossature, plancher, garde-corps, escalier.", visual: 1 },
      { label: "Plans & dossier", text: "Plan d'ensemble, nomenclature.", visual: 2 },
    ],
    deliverables: ["Modèle 3D", "Plans PDF", "Nomenclature"],
    missing: null,
  },
  {
    id: "ensemble-mecano-soude",
    num: "02",
    title: "Ensemble mécano-soudé",
    subtitle: "Portique sur roues pour équipement de manutention",
    contactType: "conception",
    nature: "Étude type · visuels illustratifs",
    need:
      "Concevoir un portique mécano-soudé déplaçable, destiné à recevoir un équipement de manutention dans l'atelier.",
    work:
      "Modélisation 3D de la poutre, des jambages contreventés et des embases sur roues, définition des interfaces et des renforts, préparation des éléments de fabrication : plans, DXF, nomenclature. Le dimensionnement réglementaire de l'appareil de levage ne fait pas partie de cette étude type.",
    hero: { ...sharpImg("portique-cao-net", 1369, 1149, "Rendu CAO d'un portique mécano-soudé sur roues"), label: "Rendu CAO", note: ILLUSTRATIVE },
    plan: { ...img("portique-plan", 680, 228, "Aperçu de plan du portique : vue de face, vue de côté et détail d'embase"), label: "Aperçu de plan", note: PLAN_NOTE },
    visuals: [
      { ...img("portique-photo", 560, 396, "Portique mécano-soudé dans un atelier"), kind: "photo", label: "Ensemble en atelier", note: ILLUSTRATIVE },
      { ...sharpImg("portique-cao-net", 1369, 1149, "Rendu CAO du portique"), kind: "cao", label: "Rendu CAO", note: ILLUSTRATIVE },
      { ...img("portique-plan", 680, 228, "Aperçu de plan du portique"), kind: "plan", label: "Aperçu de plan", note: PLAN_NOTE },
    ],
    steps: [
      { label: "Besoin / relevé", text: "Équipement à recevoir, encombrement, déplacement.", visual: 0 },
      { label: "Conception CAO", text: "Poutre, jambages, embases, interfaces.", visual: 1 },
      { label: "Plans & dossier", text: "Plans, DXF, nomenclature.", visual: 2 },
    ],
    deliverables: ["Modèle 3D", "Plans PDF", "DXF", "Nomenclature"],
    missing: null,
  },
  {
    id: "retro-conception",
    num: "03",
    title: "Rétro-conception d'un support",
    subtitle: "De la pièce de référence au modèle 3D",
    contactType: "modification",
    nature: "Étude type · visuel généré",
    need: "Reconstituer en 3D la géométrie et les interfaces d'un support existant à partir d'une pièce de référence et de relevés.",
    work:
      "Le visuel illustre la comparaison entre une pièce et une reconstruction CAO. Il s'agit d'un exemple généré : la géométrie, les cotes et le modèle ne sont pas validés pour la fabrication.",
    workLabel: "Démarche illustrée.",
    workHeading: "Démarche illustrée",
    hero: { src: "/img/retro-conception-piece-cao.jpg", width: 2816, height: 1536, alt: "Illustration de rétro-conception : support métallique et modèle CAO", label: "Pièce et CAO", note: "Visuel généré · illustratif" },
    plan: null,
    hidePlan: true,
    visuals: [
      { src: "/img/retro-conception-piece-cao.jpg", width: 2816, height: 1536, alt: "Illustration comparant un support métallique et sa représentation CAO", kind: "document", label: "Pièce et modèle CAO", note: "Illustration générée — non validée" },
    ],
    steps: [
      { label: "Observation de la pièce", text: "Repérage des surfaces et points d'interface.", visual: null },
      { label: "Relevé des interfaces", text: "Cotes à confirmer sur la pièce de référence.", visual: null },
      { label: "Reconstruction 3D", text: "Modèle à contrôler avant tout usage technique.", visual: null },
    ],
    deliverablesLabel: "Visuels présentés",
    deliverables: ["Pièce de référence illustrée", "Aperçu de modèle 3D"],
    deliverablesNote: "Illustration générée : aucune géométrie ou cote validée pour la fabrication n'est présentée.",
    missing: null,
  },
];

export const DELIVERABLES_NOTE = "Livrables disponibles sur demande — aucun téléchargement public pour ce dossier.";
